import { afterNextRender, ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from './auth.service';

@Component({
  selector: 'app-auth-controls',
  imports: [MatButtonModule, MatIconModule, MatMenuModule, TranslateModule],
  template: `
    @if (ready() && auth.user(); as user) {
      <button
        mat-button
        type="button"
        class="auth-button"
        [matMenuTriggerFor]="accountMenu"
        [disabled]="auth.pending()"
        [attr.aria-busy]="auth.pending()"
        [attr.aria-label]="'AUTH.SIGNED_IN_AS' | translate: { name: user }">
        <mat-icon class="compact-icon">account_circle</mat-icon>
        <span class="button-label">{{ auth.pending() ? ('AUTH.WORKING' | translate) : user }}</span>
      </button>
      <mat-menu #accountMenu="matMenu">
        <div class="account-summary">{{ 'AUTH.SIGNED_IN_AS' | translate: { name: user } }}</div>
        <button mat-menu-item type="button" [disabled]="auth.pending()" (click)="auth.logout()">
          <mat-icon>logout</mat-icon>
          {{ 'AUTH.LOGOUT' | translate }}
        </button>
      </mat-menu>
      <button
        mat-button
        type="button"
        class="auth-button feedback-button"
        [disabled]="auth.pending()"
        [attr.aria-label]="'ERROR_REPORTING.FEEDBACK.BUTTON' | translate"
        (click)="feedbackRequested.emit()">
        <mat-icon class="compact-icon">feedback</mat-icon>
        <span class="button-label">{{ 'ERROR_REPORTING.FEEDBACK.BUTTON' | translate }}</span>
      </button>
    } @else {
      <button
        mat-button
        type="button"
        class="auth-button"
        [disabled]="!ready() || auth.pending()"
        [attr.aria-busy]="ready() && auth.pending()"
        [attr.aria-label]="(ready() && auth.pending() ? 'AUTH.WORKING' : 'AUTH.LOGIN') | translate"
        (click)="auth.login()">
        <mat-icon class="compact-icon">{{ ready() && auth.pending() ? 'hourglass_top' : 'login' }}</mat-icon>
        <span class="button-label">{{ (ready() && auth.pending() ? 'AUTH.WORKING' : 'AUTH.LOGIN') | translate }}</span>
      </button>
    }
    @if (ready() && auth.error(); as error) {
      <p role="alert" class="auth-error">{{ error | translate }}</p>
    }
  `,
  styles: `
    :host {
      display: flex;
      align-items: center;
      min-width: 0;
    }

    .auth-button {
      min-width: 40px;
      padding: 0 8px;
    }

    .button-label {
      display: block;
      max-width: 10rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .compact-icon {
      display: none;
      margin: 0;
    }

    .account-summary {
      padding: 8px 16px;
      max-width: 260px;
      overflow-wrap: anywhere;
      font-size: 14px;
    }

    .auth-error {
      position: fixed;
      top: calc(var(--header-height, 64px) + 8px);
      right: 16px;
      width: min(360px, calc(100vw - 32px));
      margin: 0;
      padding: 12px 16px;
      border: 1px solid #b91c1c;
      border-radius: 8px;
      background: #fff;
      color: #991b1b;
      font-size: 14px;
      white-space: normal;
      box-shadow: 0 4px 12px #0002;
    }

    @media (max-width: 600px) {
      .compact-icon {
        display: inline-block;
      }

      .button-label {
        display: none;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthControlsComponent {
  readonly feedbackRequested = output<void>();
  protected readonly auth = inject(AuthService);
  protected readonly ready = signal(false);

  constructor() {
    afterNextRender(() => this.ready.set(true));
  }
}
